import { useEffect, useRef, useState } from 'react';
import { ActionIcon, TextInput } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';
import { DateField } from '../../shared/ui/DateField';

/** ДД.ММ.ГГГГ ↔ ISO YYYY-MM-DD. */
const ruToIso = (s: string): string => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec((s ?? '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};
const isoToRu = (s: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s ?? '').trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/**
 * Поле даты для ЭТрН: календарь/ручной ввод, но хранит значение в формате
 * `ДД.ММ.ГГГГ` (как требует XML), а не ISO.
 */
export function EtrnDateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <DateField
      label={label}
      value={ruToIso(value)}
      onChange={(iso) => onChange(isoToRu(iso))}
    />
  );
}

/** `datetime-local` (YYYY-MM-DDTHH:MM) → `ДД.ММ.ГГГГTчч:мм:сс+03:00` и обратно. */
function localToEtrn(local: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return '';
  return `${m[3]}.${m[2]}.${m[1]}T${m[4]}:${m[5]}:00+03:00`;
}
function etrnToLocal(v: string): string {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})T(\d{2}):(\d{2})/.exec((v ?? '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}` : '';
}
/** Показ значения в человекочитаемом виде «ДД.ММ.ГГГГ чч:мм». */
function etrnToText(v: string): string {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})T(\d{2}):(\d{2})/.exec((v ?? '').trim());
  return m ? `${m[1]}.${m[2]}.${m[3]} ${m[4]}:${m[5]}` : '';
}

/**
 * Дата и время для ЭТрН: календарь с временем (нативный), хранит значение в
 * формате `ДД.ММ.ГГГГTчч:мм:сс+03:00`.
 */
export function EtrnDateTimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [text, setText] = useState(etrnToText(value));
  const nativeRef = useRef<HTMLInputElement>(null);
  useEffect(() => setText(etrnToText(value)), [value]);

  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        label={label}
        placeholder="ДД.ММ.ГГГГ чч:мм"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onBlur={() => {
          const m = /^(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2})/.exec(text.trim());
          if (m) onChange(`${m[1]}.${m[2]}.${m[3]}T${m[4]}:${m[5]}:00+03:00`);
          else if (!text.trim()) onChange('');
          setText(etrnToText(value));
        }}
        rightSection={
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Открыть календарь"
            onClick={() => nativeRef.current?.showPicker?.()}
          >
            <IconCalendar size={16} />
          </ActionIcon>
        }
      />
      <input
        ref={nativeRef}
        type="datetime-local"
        value={etrnToLocal(value)}
        tabIndex={-1}
        aria-hidden
        onChange={(e) => onChange(localToEtrn(e.currentTarget.value))}
        style={{
          position: 'absolute',
          right: 8,
          bottom: 6,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          border: 0,
          padding: 0,
        }}
      />
    </div>
  );
}
